import { PaginationLinksModel } from './PaginationLinks';

export type CustomerType = 'person' | 'organization';

export const CUSTOMER_TYPES = {
  PERSON: 'person',
  ORGANIZATION: 'organization',
} as const;

/** A project a contact takes part in (resolved client-side from the project list). */
export declare interface ContactLeadModel {
  id: string;
  title?: string | null;
  role?: string | null;
  is_primary?: boolean;
}

/** A customer in short, as contact list rows and project responses carry it. */
export declare interface ContactCustomerSummary {
  id: string;
  type?: CustomerType;
  company_name?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
}

/** A customer a contact is linked to (`GET /contacts` rows): the role there, and whether it's that customer's primary. */
export declare interface ContactCustomerLink extends ContactCustomerSummary {
  role?: string | null;
  is_primary?: boolean;
}

/** The person an appointment is with (`service_requests.contact_id`), as service request responses carry it. */
export declare interface ServiceRequestContactModel {
  id: string;
  fullname?: string | null;
  email?: string | null;
  phone?: string | null;
}

export declare interface ContactModel {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  fullname?: string | null;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  locale?: string | null;
  /** Role at the customer in context (customer and project responses; per customer in `customers`). */
  role?: string | null;
  notes?: string | null;
  /** Primary contact of the customer in context — not of a project (see `LeadModel.primary_contact_id`). */
  is_primary?: boolean;
  created?: string;
  modified?: string;

  /** Every (visible) customer the contact is linked to; only on `GET /contacts` rows. */
  customers?: ContactCustomerLink[];
  /** The customer in context, set client-side when known but not embedded. */
  customer_id?: string;
  /** Projects the contact is on, resolved client-side (no endpoint returns them). */
  leads?: ContactLeadModel[];
}

export type ContactPayload = Partial<
  Pick<
    ContactModel,
    'firstname' | 'lastname' | 'email' | 'phone' | 'gender' | 'locale' | 'role' | 'notes'
  >
> & {
  /** Makes it the customer's primary contact (mirrored onto the customer) — never the project's. */
  is_primary?: boolean;
};

/** `POST /customers/{id}/contacts` with an existing contact of the partner: links it (or updates the link). */
export type ContactLinkPayload = {
  contact_id: string;
  /** Leave out to keep the role; `null` clears it. */
  role?: string | null;
  is_primary?: boolean;
};

export declare interface ContactListModel {
  items: ContactModel[];
  links?: PaginationLinksModel;
  total?: number;
  total_pages?: number;
}
